import Alpine from "alpinejs";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";
import scrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(scrollTrigger);
gsap.registerPlugin(MorphSVGPlugin);
gsap.registerPlugin(CustomEase);

const AKADORI_MV_IMAGE01_CLIP_PATHS = [
  "M322 132.049C322 231.111 298.224 283 197.004 283C80 283 14 251 6 136C3 60 66 5 154.885 5C224.855 7 322 38 322 132.049Z",
  "M322 137C318 225 288 279 194 283C84 286 16 251 7 139C4 64 62 10 154 8C232 5 316 45 322 137Z",
  "M322 128C320 234 291 280 202 279C82 277 13 258 7 133C4 59 74 11 160 11C238 12 324 36 322 128Z",
];

const AKADORI_MV_IMAGE02_CLIP_PATHS = [
  "M368 145.951C368 251.639 335.758 307 158 307C20.1875 307 -44.3636 276.131 -52 145.951C-52 57.0058 -13.394 0 158 0C273.98 0 368 43.978 368 145.951Z",
  "M368 150C364 248 330 304 160 307C28 307 -40 276 -47 150C-49 64 -8 5 158 4C278 3 370 48 368 150Z",
  "M368 141C366 255 334 307 166 302C26 298 -45 268 -48 142C-50 62 5 7 162 8C286 9 371 38 368 141Z",
];
const AKADORI_MV_INNER_IMAGE_INITIAL_SCALE = 1.5;
const AKADORI_MV_IMAGE_POP_EASE = CustomEase.create(
  "akadoriMvImagePopEase",
  "M0,0 C0.12,0.72 0.22,1.18 0.42,1.08 C0.54,1.01 0.6,0.965 0.7,0.988 C0.8,1.012 0.9,1.004 1,1"
);

const playAkadoriPersonFrameLoop = (
  personFrame1: HTMLElement,
  personFrame2: HTMLElement
) => {
  gsap
    .timeline({
      delay: 0,
      repeat: -1,
    })
    .to({}, {
      duration: 0.5,
      ease: "steps(1)",
      onStart: () => {
        gsap.set(personFrame1, { opacity: 0 });
        gsap.set(personFrame2, { opacity: 1 });
      },
    })
    .to({}, {
      duration: 2,
      ease: "steps(1)",
      onStart: () => {
        gsap.set(personFrame1, { opacity: 1 });
        gsap.set(personFrame2, { opacity: 0 });
      },
    });
};

const splitTextToChars = (
  element: HTMLElement,
  className = "akadori-mv-title__char"
) => {
  const chars: HTMLElement[] = [];

  const splitNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || "").replace(/[\t\r\n]+/g, "").trim();

      if (!text) {
        return;
      }

      const fragment = document.createDocumentFragment();

      Array.from(text).forEach((char) => {
        const charElement = document.createElement("span");
        charElement.className = className;
        charElement.style.display = "inline-block";
        charElement.textContent = char === " " ? "\u00A0" : char;
        fragment.appendChild(charElement);
        chars.push(charElement);
      });

      node.parentNode?.replaceChild(fragment, node);
      return;
    }

    Array.from(node.childNodes).forEach(splitNode);
  };

  Array.from(element.childNodes).forEach(splitNode);

  return chars;
};

const playAfterTextReady = (callback: () => void) => {
  const playOnNextPaint = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  };

  if (document.fonts) {
    document.fonts.ready.then(playOnNextPaint).catch(playOnNextPaint);
    return;
  }

  playOnNextPaint();
};

export const initAkadoriMv = () => {
  Alpine.data("akadoriMv", () => {
    return {
      init() {
        const title = this.$refs.title as HTMLElement;
        const name = this.$refs.name as HTMLElement;
        const person = this.$refs.person as HTMLElement;
        const personFrame1 = this.$refs.personFrame1 as HTMLElement;
        const personFrame2 = this.$refs.personFrame2 as HTMLElement;
        const sunset = this.$refs.sunset as HTMLElement;
        const mountain = this.$refs.mountain as HTMLElement;
        const image01 = this.$refs.image01 as HTMLElement;
        const image01Clip = this.$refs.image01Clip as unknown as SVGPathElement;
        const image01Image = this.$refs.image01Image as unknown as SVGImageElement;
        const image02 = this.$refs.image02 as HTMLElement;
        const image02Clip = this.$refs.image02Clip as unknown as SVGPathElement;
        const image02Image = this.$refs.image02Image as unknown as SVGImageElement;
        const text = this.$refs.text as HTMLElement;
        const figure = this.$refs.figure as HTMLElement;
        const titleChars = splitTextToChars(title);
        const textParagraphs = Array.from(text.querySelectorAll<HTMLElement>("p"));
        const textCharGroups = textParagraphs.map((paragraph) =>
          splitTextToChars(paragraph, "akadori-mv-text__char")
        );
        const textChars: HTMLElement[] = [];
        textCharGroups.forEach((chars) => {
          chars.forEach((char) => textChars.push(char));
        });
    
        gsap.set(titleChars, {
          opacity: 0,
          yPercent: 80,
          transformOrigin: "50% 100%",
          force3D: true,
          willChange: "transform, opacity",
        });
        gsap.set(title, {
          autoAlpha: 1,
        });
        gsap.set(name, {
          opacity: 0,
          xPercent: -10,
        });
        gsap.set(person, {
          opacity: 0,
          xPercent: -10,
        });
        gsap.set(personFrame1, { opacity: 1 });
        gsap.set(personFrame2, { opacity: 0 });
        gsap.set(sunset, {
          opacity: 0,
          yPercent: 0,
        });
        gsap.set(mountain, {
          opacity: 0,
          yPercent: 10,
        });
        gsap.set(image01, {
          opacity: 0,
          scale: 0.6,
          transformOrigin: "50% 50%",
        });
        gsap.set(image01Clip, {
          attr: { d: AKADORI_MV_IMAGE01_CLIP_PATHS[2] },
        });
        gsap.set(image01Image, {
          scale: AKADORI_MV_INNER_IMAGE_INITIAL_SCALE,
          svgOrigin: "161 141.5",
        });
        gsap.set(image02, {
          opacity: 0,
          scale: 0.5,
          transformOrigin: "50% 50%",
        });
        gsap.set(image02Clip, {
          attr: { d: AKADORI_MV_IMAGE02_CLIP_PATHS[2] },
        });
        gsap.set(image02Image, {
          scale: AKADORI_MV_INNER_IMAGE_INITIAL_SCALE,
          svgOrigin: "158 153.5",
        });
        gsap.set(text, {
          opacity: 1,
        });
        gsap.set(textParagraphs, {
          opacity: 0,
        });
        gsap.set(textChars, {
          opacity: 0.3,
        });
        gsap.set(figure, {
          opacity: 0,
          yPercent: 10,
        });
        const isDesktop = window.matchMedia("(min-width: 750px)").matches;
        const addImage01Animation = (
          timeline: gsap.core.Timeline,
          startPosition: string
        ) => {
          timeline
            .addLabel("image01In", startPosition)
            .to(image01, {
              opacity: 1,
              duration: 0.01,
              ease: "none",
            },
            "image01In"
            )
            .to(image01, {
              scale: 1,
              duration: 1.65,
              ease: AKADORI_MV_IMAGE_POP_EASE,
            },
            "image01In"
            )
            .to(image01Clip, {
              duration: 1.8,
              ease: "sine.inOut",
              morphSVG: {
                shape: AKADORI_MV_IMAGE01_CLIP_PATHS[0],
                shapeIndex: 0,
              },
            },
            "image01In+=0.25"
            )
            .to(image01Image, {
              scale: 1,
              duration: 2.3,
              ease: AKADORI_MV_IMAGE_POP_EASE,
            },
            "image01In+=0.1"
            );

          return timeline;
        };

        let image01TextTriggerReady = false;
        const setupImage01TextTrigger = () => {
          if (image01TextTriggerReady) {
            return;
          }

          image01TextTriggerReady = true;
          const image01TextTl = gsap.timeline({
            scrollTrigger: {
              trigger: text,
              start: "top 85%",
              once: true,
            },
          });

          addImage01Animation(image01TextTl, "0");
        };

        let textParagraphTriggersReady = false;
        const setupTextParagraphTriggers = () => {
          if (textParagraphTriggersReady) {
            return;
          }

          textParagraphTriggersReady = true;

          textParagraphs.forEach((paragraph, index) => {
            const chars = textCharGroups[index];
            const paragraphTl = gsap.timeline({
              scrollTrigger: {
                trigger: paragraph,
                start: "top 95%",
                once: true,
              },
            });

            paragraphTl
              .to(paragraph, {
                opacity: 1,
                duration: 0.01,
                ease: "none",
              })
              .to(chars, {
                opacity: 1,
                duration: 1.1,
                ease: "power2.inOut",
                stagger: 0.03,
              });
          });
        };

        const tl = gsap.timeline({ paused: true });
        const figureTl = gsap.timeline({
          scrollTrigger: {
            trigger: figure,
            start: "top 80%",
            once: true,
          },
        });
        const image02Tl = gsap.timeline({
          scrollTrigger: {
            trigger: image02,
            start: "top 80%",
            once: true,
          },
        });

        image02Tl
          .to(image02, {
            opacity: 1,
            duration: 0.01,
            ease: "none",
          })
          .to(image02, {
            scale: 1,
            duration: 1.65,
            ease: AKADORI_MV_IMAGE_POP_EASE,
          },
          "<"
          )
          .to(image02Clip, {
            duration: 1.8,
            ease: "sine.inOut",
            morphSVG: {
              shape: AKADORI_MV_IMAGE02_CLIP_PATHS[0],
              shapeIndex: 0,
            },
          },
          "0.25"
          )
          .to(image02Image, {
            scale: 1,
            duration: 2.3,
            ease: AKADORI_MV_IMAGE_POP_EASE,
          },
          "0.1"
          );

        setupTextParagraphTriggers();

        if (isDesktop) {
          setupImage01TextTrigger();
        }

        tl.to(titleChars, {
          opacity: 1,
          yPercent: 0,
          duration: 0.7,
          ease: "back.out(1.45)",
          stagger: 0.03,
          onComplete: () => {
            gsap.set(titleChars, { willChange: "auto" });
          },
        },
        "0"
        )
        .to(name, {
          opacity: 1,
          xPercent: 0,
          duration: 1.2,
          ease: "power2.inOut",
        },
        "-=0.6"
        )
        .to(person, {
          opacity: 1,
          xPercent: 0,
          duration: 1.2,
          ease: "power2.inOut",
          onComplete: () => {
            playAkadoriPersonFrameLoop(personFrame1, personFrame2);
          },
        },
        "<"
        )
        .to(mountain, {
          opacity: 1,
          yPercent: 0,
          duration: 1.2,
          stagger: 0.3,
          ease: "power2.inOut",
        },
        "0"
        )
        .to(sunset, {
          opacity: 1,
          yPercent: -70,
          duration: 1.4,
          ease: "power2.out",
        },
        "0.7"
        );

        if (!isDesktop) {
          addImage01Animation(tl, "-=1.2");
        }

        figureTl.to(figure, {
          opacity: 1,
          yPercent: 0,
          duration: 1.2,
          ease: "power2.out",
        });

        playAfterTextReady(() => {
          tl.play();
        });
      },
    };
  });
};
